import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/header";
import Footer from '../components/footer';
import FloatingChatButton from '../components/FloatingChatButton';
import CallbackPopup from '../components/CallbackPopup';
import focusedSpecialityData from "../data/focusedSpecialityData";
import StarDoctorsIndia from "./StarDoctorsIndia";
import TopHospitals from "./TopHospitalsIndia";
import TopHospitalsTurkey from "./TopHospitalsTurkey"; // You must create this
import SpecialtyDoctorsCarousel from "../components/SpecialtyDoctorsCarousel";
import TopHospitalsIndia from "../components/TopHospitalsIndia";
import TopHospitalsTurkeyData from "../components/TopHospitalsTurkeyData";

const FocusedSpeciality = () => {
  const { name } = useParams();
  const navigate = useNavigate();
  const [isCallbackPopupOpen, setIsCallbackPopupOpen] = useState(false);

  // Function to convert treatment name to slug
  const generateSlug = (treatmentName) => {
    return treatmentName
      .toLowerCase()
      .replace(/[^a-zA-Z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .trim('-'); // Remove leading/trailing hyphens
  };

  // Function to handle treatment click
  const handleTreatmentClick = (treatmentName) => {
    const slug = generateSlug(treatmentName);
    navigate(`/treatments/${slug}`);
  };

  // Function to handle opening callback popup
  const handleGetSecondOpinion = () => {
    setIsCallbackPopupOpen(true);
  };

  // Function to handle closing callback popup
  const handleCloseCallbackPopup = () => {
    setIsCallbackPopupOpen(false);
  };

  const tabs = useMemo(
    () => [
      "Cardiology",
      "Cosmetics",
      "Gynaecology",
      "Hematology",
      "Fertility",
      "Oncology",
      "Neurosurgery",
      "Orthopedics",
      "Liver",
      "Kidney",
      "Weight Loss",
    ],
    []
  );

  const formattedName =
    name?.replace(/-/g, " ")?.replace(/\b\w/g, (c) => c.toUpperCase()) || "";

  const [activeTab, setActiveTab] = useState("Cardiology");

  useEffect(() => {
    if (tabs.includes(formattedName)) {
      setActiveTab(formattedName);
    } else {
      setActiveTab("Cardiology");
    }

    window.scrollTo(0, 0);
  }, [formattedName, tabs]);

  const { image, points } = focusedSpecialityData[activeTab] || {
    image: "/images/focus/default.png",
    points: [],
  };

  return (
    <>
      <Header />

      {/* Banner Section */}
      <section className="specialitiesNew-Sec py-5">
        <div className="container mx-auto px-4">
          <div className="titleHeading text-center mb-4 flex flex-col items-center justify-center">
            <h2 className="text-4xl font-bold mb-4 text-center">
              <span className="text-[#1976f2]">Focussed</span>{" "}
              <span className="text-black">Specialities</span>
            </h2>
            <p className="text-muted mt-2 text-center max-w-2xl">
              Explore top medical specialities and connect with the best doctors
              and hospitals.
            </p>
          </div>
        </div>

        <div className="w-full">
          <img
            src="/images/specialisation-main-banner.webp"
            className="w-full h-auto object-cover"
            alt="Medical Specialties Banner"
            loading="lazy"
          />
        </div>
      </section>

      {/* Tab Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          {/* Top Heading */}
          <h2 className="text-5xl font-bold text-center mb-12">
            <span className="text-[#1976f2]">Search for</span>{" "}
            <span className="text-black">your treatment</span>
          </h2>

          {/* Tabs replaced with Image-based specialties */}
          <div className="flex justify-center mb-8">
            <div className="flex justify-center items-center">
              
              {/* ALL ITEMS IN SINGLE ROW */}
              <div className="flex justify-center flex-wrap gap-4 max-w-7xl">
                {[
                  { name: 'Cardiology', imgSrc: '/images/image copy 43.png' },
                  { name: 'Cosmetics', imgSrc: '/images/image copy 44.png' },
                  { name: 'Gynaecology', imgSrc: '/images/image copy 45.png' },
                  { name: 'Hematology', imgSrc: '/images/image copy 46.png' },
                  { name: 'Fertility', imgSrc: '/images/image copy 47.png' },
                  { name: 'Oncology', imgSrc: '/images/image copy 48.png' },
                  { name: 'Neurosurgery', imgSrc: '/images/image copy 50.png' },
                  { name: 'Orthopedics', imgSrc: '/images/focussedortho.png' },
                  { name: 'Liver', imgSrc: '/images/image copy 51.png' },
                  { name: 'Kidney', imgSrc: '/images/image copy 52.png' },
                  { name: 'Weight Loss', imgSrc: '/images/image copy 53.png' },
                ].map((speciality) => (
                  <div 
                    key={speciality.name}
                    className={`transition-all duration-300 hover:scale-105 cursor-pointer ${
                      activeTab === speciality.name 
                        ? 'ring-4 ring-[#1976f2] rounded-xl scale-110' 
                        : 'hover:ring-2 hover:ring-gray-300 rounded-xl'
                    }`}
                    onClick={() => setActiveTab(speciality.name)}
                  >
                    <img 
                      src={speciality.imgSrc} 
                      alt={speciality.name} 
                      className={`object-contain rounded-xl shadow-md transition-all duration-300 ${
                        activeTab === speciality.name ? 'w-28 h-auto' : 'w-24 h-auto'
                      }`}
                    />
                  </div>
                ))}
              </div>
              
            </div>
          </div>

          {/* Main Content */}
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left - Bullet Points */}
            <div>
              <h3 className="text-2xl font-bold text-black mb-2">
                {activeTab}
              </h3>
              <br></br>

              <div className="grid grid-cols-2 gap-4 mb-6">
                {points?.map((point, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 text-gray-700 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors duration-200"
                    onClick={() => handleTreatmentClick(point)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleTreatmentClick(point);
                      }
                    }}
                  >
                    <span className="text-[#1976f2] text-xl">•</span>
                    <p className="text-black hover:text-[#1976f2] transition-colors duration-200">{point}</p>
                  </div>
                ))}
              </div>

              {/* Free Second Opinion Button */}
              <div>
                <button 
                  className="bg-[#1976f2] text-white px-6 py-2 rounded-full font-semibold hover:bg-[#135ecf] transition"
                  onClick={handleGetSecondOpinion}
                >
                  Get Free Second Opinion
                </button>
              </div>
            </div>

            {/* Right - Image */}
            <div className="flex justify-center">
              <img
                src={image}
                alt={activeTab}
                className="w-full max-w-md rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-4xl font-bold text-center mb-12">
          <span className="text-[#1976f2]">Best {activeTab} Doctors</span> From Around The World
        </h2>
        <SpecialtyDoctorsCarousel specialties={[activeTab]} />
      </section>
      <section className="bg-gray-100 py-12 getStartedPrefHosp expertConsultationSec">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-10 items-stretch">
            {/* Left - Cost Estimate Form */}
            <div className="flex flex-col justify-between bg-white p-6 md:p-10 rounded-xl shadow-md h-full">
              <div>
                <h2 className="text-2xl font-semibold text-black mb-6">
                  Get a cost estimate for your heart treatment
                </h2>

                <form className="space-y-4">
                  {/* Full Name */}
                  <input
                    type="text"
                    placeholder="Full Name"
                    className="w-full p-3 border border-gray-300 rounded"
                  />

                  {/* Email */}
                  <input
                    type="email"
                    placeholder="Email"
                    className="w-full p-3 border border-gray-300 rounded"
                  />

                  {/* Mobile Number with Code */}
                  <div className="flex gap-4">
                    <input
                      type="text"
                      placeholder="+91"
                      className="w-1/3 p-3 border border-gray-300 rounded"
                    />
                    <input
                      type="tel"
                      placeholder="Mobile Number"
                      className="w-2/3 p-3 border border-gray-300 rounded"
                    />
                  </div>

                  {/* Country & City */}
                  <div className="flex gap-4">
                    <input
                      type="text"
                      placeholder="Country"
                      className="w-1/2 p-3 border border-gray-300 rounded"
                    />
                    <input
                      type="text"
                      placeholder="City"
                      className="w-1/2 p-3 border border-gray-300 rounded"
                    />
                  </div>

                  {/* Treatment Details */}
                  <textarea
                    placeholder="Treatment Details"
                    rows="3"
                    className="w-full p-3 border border-gray-300 rounded"
                  ></textarea>

                  {/* Submit Button */}
                  <div className="text-center">
                    <button
                      type="submit"
                      className="bg-[#1976f2] text-white px-8 py-2 rounded-full hover:bg-blue-700 transition"
                    >
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Right - Expert Consultation Blue Box */}
            <div className="bg-[#1976f2] text-white p-6 md:p-10 rounded-xl shadow-md h-full flex flex-col justify-between">
              <div>
                <h4 className="text-5xl font-bold mb-4">
                  Book your expert consultation now
                </h4>

                <div className="bookConsultationImageWrap mb-4">
                  <img
                    src="/images/india3.webp"
                    alt="Book Expert Consultation"
                    className="rounded-lg shadow-md w-full max-w-md mx-auto md:mx-0"
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start mt-4">
                <button
                  className="bg-white text-black px-6 py-2 rounded-full hover:bg-gray-200 transition"
                  data-bs-toggle="modal"
                  data-bs-target="#getInTouchFormModal"
                >
                  Get a call back
                </button>
                <a
                  href="https://wa.me/9599222655"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-500 text-white px-6 py-2 rounded-full hover:bg-green-600 flex items-center gap-2 justify-center"
                >
                  <i className="ri-whatsapp-line"></i> Chat Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      <TopHospitalsIndia />
      <TopHospitalsTurkeyData />

      <Footer />
      <FloatingChatButton />
      <CallbackPopup 
        isOpen={isCallbackPopupOpen} 
        onClose={handleCloseCallbackPopup} 
      />
    </>
  );
};

export default FocusedSpeciality;
